import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { TemplateBar } from "@/components/TemplateBar";
import { GenerationErrorBlock } from "@/components/GenerationErrorBlock";
import { Button } from "@/components/ui/button";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Lightning, Plus, Spinner, FloppyDisk } from "@phosphor-icons/react";
import { UsageWarningBanner } from "@/components/UsageWarningBanner";
import { UpgradeModal } from "@/components/UpgradeModal";
import { AlwaysIncludeIndicator } from "@/components/AlwaysIncludeIndicator";
import { useLocalDraft } from "@/hooks/useLocalDraft";
import { CONTENT_TYPES, PLATFORMS } from "@/lib/generateConstants";
import { BulkOutputSlot } from "@/components/generate/BulkOutputSlot";
import { BulkSharedInputs } from "@/components/generate/BulkSharedInputs";
import { BulkProgressBar } from "@/components/generate/BulkProgressBar";

export default function BulkGeneratePage() {
  const { api, refreshUser } = useAuth();
  const { restore, save: saveDraft, clear: clearDraft } = useLocalDraft("nib_draft_bulk");

  const [topic, setTopic] = useState("");
  const [researchText, setResearchText] = useState("");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [cta, setCta] = useState("");
  const [toneVariations, setToneVariations] = useState(["Direct", "Practical"]);
  const [aiModel, setAiModel] = useState("");
  const [availableModels, setAvailableModels] = useState([]);

  const [outputs, setOutputs] = useState([
    { id: "1", content_type: "Thought Leadership", platform: "LinkedIn" },
    { id: "2", content_type: "Email (Single)", platform: "Email List" },
  ]);

  const [generating, setGenerating] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [results, setResults] = useState({});
  const [savedIds, setSavedIds] = useState({});
  const [copiedIds, setCopiedIds] = useState({});
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [alwaysIncludeFiles, setAlwaysIncludeFiles] = useState([]);
  const [generationError, setGenerationError] = useState(null);
  const [autoRetryCount, setAutoRetryCount] = useState(0);

  useEffect(() => {
    const draft = restore();
    if (draft) {
      if (draft.topic) setTopic(draft.topic);
      if (draft.researchText) setResearchText(draft.researchText);
      if (draft.audience) setAudience(draft.audience);
      if (draft.goal) setGoal(draft.goal);
      if (draft.cta) setCta(draft.cta);
      if (draft.toneVariations?.length) setToneVariations(draft.toneVariations);
      if (draft.aiModel) setAiModel(draft.aiModel);
      if (draft.outputs?.length) setOutputs(draft.outputs);
    }
  }, [restore]);

  useEffect(() => {
    saveDraft({ topic, researchText, audience, goal, cta, toneVariations, aiModel, outputs });
  }, [topic, researchText, audience, goal, cta, toneVariations, aiModel, outputs, saveDraft]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const resp = await api.get("/models/available");
        setAvailableModels(resp.data.models || []);
        if (resp.data.models?.length > 0) setAiModel(resp.data.models[0]);
      } catch (e) { console.error("Failed to fetch models:", e); }
    };
    fetchModels();
  }, [api]);

  useEffect(() => {
    const loadDefaults = async () => {
      try {
        const resp = await api.get("/profile");
        const p = resp.data;
        setAudience(prev => prev || p.default_audience || "");
        setToneVariations(prev => prev.length > 0 ? prev : (p.default_tone_variations || []));
        setAiModel(prev => prev || p.default_ai_model || "");
        setCta(prev => prev || p.default_cta || "");
      } catch (e) { console.error("Failed to load defaults:", e); }
    };
    loadDefaults();
  }, [api]);

  const addOutput = () => {
    if (outputs.length >= 10) { toast.error("Maximum 10 outputs per batch"); return; }
    setOutputs((prev) => [...prev, { id: String(Date.now()), content_type: "", platform: "" }]);
  };

  const removeOutput = (id) => { if (outputs.length <= 1) return; setOutputs((prev) => prev.filter((o) => o.id !== id)); };
  const updateOutput = (id, field, value) => { setOutputs((prev) => prev.map((o) => (o.id === id ? { ...o, [field]: value } : o))); };
  const toggleTone = (tone) => { setToneVariations((prev) => prev.includes(tone) ? prev.filter((t) => t !== tone) : [...prev, tone]); };

  const validOutputs = outputs.filter((o) => o.content_type && o.platform);

  const handleGenerateAll = async () => {
    if (!topic || !audience || !goal || !aiModel) { toast.error("Fill in all shared fields first"); return; }
    if (toneVariations.length === 0) { toast.error("Select at least one tone"); return; }
    if (validOutputs.length === 0) { toast.error("Add at least one output with type and platform"); return; }

    setGenerating(true); setResults({}); setSavedIds({}); setCopiedIds({}); setGenerationError(null); setAutoRetryCount(0);

    let hadStructuredError = false;
    for (let i = 0; i < validOutputs.length; i++) {
      setCurrentIdx(i);
      const output = validOutputs[i];
      try {
        const resp = await api.post("/generate", {
          topic, research_text: researchText, content_type: output.content_type, platform: output.platform,
          audience, goal, cta: cta || "NONE", offer_asset: "NONE", tone_variations: toneVariations,
          length_constraint: "PLATFORM BEST LENGTH", ai_model: aiModel, file_ids: [],
        });
        setResults((prev) => ({ ...prev, [output.id]: { success: true, data: resp.data } }));
      } catch (err) {
        const data = err.response?.data;
        if (data?.error && data?.error_type) {
          setResults((prev) => ({ ...prev, [output.id]: { success: false, error: data.error_message } }));
          if (!hadStructuredError) { setGenerationError(data); hadStructuredError = true; }
          if (data.error_type === "rate_limit") { setShowUpgrade(true); break; }
          if (data.error_type === "invalid_key") break;
        } else {
          if (err.response?.status === 429) { setShowUpgrade(true); break; }
          setResults((prev) => ({ ...prev, [output.id]: { success: false, error: data?.detail || "Generation failed" } }));
        }
      }
    }

    setCurrentIdx(-1); setGenerating(false);
    const completed = Object.values(results).filter((r) => r?.success).length;
    if (completed > 0) clearDraft();
    toast.success(`Batch complete! Generated ${validOutputs.length} piece${validOutputs.length > 1 ? "s" : ""}`);
    refreshUser();
  };

  const handleSave = async (output) => {
    const result = results[output.id];
    if (!result?.success) return;
    try {
      await api.post("/history", {
        title: `${output.content_type} - ${topic.substring(0, 60)}`, topic, research_material: researchText,
        content_type: output.content_type, platform: output.platform, audience, goal, cta: cta || "NONE",
        tone_variations: toneVariations, ai_model_used: aiModel,
        generated_core_angle: result.data.core_angle || "", generated_draft: result.data.draft || "",
        generated_why_it_works: result.data.why_it_works || "",
        generated_alternate_hooks: result.data.alternate_hooks || [], generated_cta_variations: result.data.cta_variations || null,
      });
      setSavedIds((prev) => ({ ...prev, [output.id]: true }));
      toast.success(`Saved "${output.content_type}" to history`);
    } catch { toast.error("Failed to save"); }
  };

  const handleCopy = async (output) => {
    const result = results[output.id];
    if (!result?.success) return;
    const text = result.data.draft || "";
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopiedIds((prev) => ({ ...prev, [output.id]: true }));
    setTimeout(() => setCopiedIds((prev) => ({ ...prev, [output.id]: false })), 2000);
    toast.success("Draft copied!");
  };

  const handleSaveAll = async () => {
    let count = 0;
    for (const output of validOutputs) {
      if (results[output.id]?.success && !savedIds[output.id]) { await handleSave(output); count++; }
    }
    if (count > 0) toast.success(`Saved ${count} items to history`);
  };

  const completedCount = Object.keys(results).length;
  const successCount = Object.values(results).filter((r) => r.success).length;
  const progress = validOutputs.length > 0 ? (completedCount / validOutputs.length) * 100 : 0;

  return (
    <NoBusinessGuard>
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="bulk-generate-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight" data-testid="bulk-page-title">Bulk Generate</h1>
            <p className="text-sm text-[var(--text-muted)] mt-0.5">Generate multiple content pieces from one topic</p>
          </div>
          <TemplateBar
            builderType="bulk"
            getConfig={() => ({ audience, goal, cta, tone_variations: toneVariations, ai_model: aiModel, outputs: outputs.map((o) => ({ content_type: o.content_type, platform: o.platform })) })}
            applyConfig={(cfg) => {
              if (cfg.audience) setAudience(cfg.audience); if (cfg.goal) setGoal(cfg.goal);
              if (cfg.cta) setCta(cfg.cta); if (cfg.tone_variations) setToneVariations(cfg.tone_variations);
              if (cfg.ai_model) setAiModel(cfg.ai_model);
              if (cfg.outputs?.length) setOutputs(cfg.outputs.map((o, i) => ({ id: String(Date.now() + i), ...o })));
            }}
          />
        </div>

        <UsageWarningBanner />

        <BulkSharedInputs
          topic={topic} setTopic={setTopic} researchText={researchText} setResearchText={setResearchText}
          audience={audience} setAudience={setAudience} goal={goal} setGoal={setGoal}
          cta={cta} setCta={setCta} aiModel={aiModel} setAiModel={setAiModel} availableModels={availableModels}
          toneVariations={toneVariations} toggleTone={toggleTone}
        />

        {/* Output slots */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">Outputs ({outputs.length}/10)</label>
            <button type="button" onClick={addOutput} disabled={outputs.length >= 10}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-[var(--accent-subtle)] border border-[#FF4400]/20 text-[#FF4400] hover:bg-[var(--accent-subtle)] transition-colors disabled:opacity-30"
              data-testid="add-output-button">
              <Plus size={12} />Add Output
            </button>
          </div>
          <AnimatePresence>
            {outputs.map((output, idx) => (
              <BulkOutputSlot key={output.id} output={output} idx={idx}
                isCurrentlyGenerating={generating && currentIdx === validOutputs.findIndex((v) => v.id === output.id)}
                result={results[output.id]} contentTypes={CONTENT_TYPES} platforms={PLATFORMS}
                onUpdateOutput={updateOutput} onRemoveOutput={removeOutput}
                onCopy={handleCopy} onSave={handleSave}
                copiedId={copiedIds[output.id]} savedId={savedIds[output.id]} outputCount={outputs.length} />
            ))}
          </AnimatePresence>
        </div>

        {generating && <BulkProgressBar currentIdx={currentIdx} totalOutputs={validOutputs.length} progress={progress} />}

        <AlwaysIncludeIndicator onFilesLoaded={setAlwaysIncludeFiles} />

        <AnimatePresence>
          {generationError && !generating && (
            <GenerationErrorBlock error={generationError} onRetry={handleGenerateAll}
              onDismiss={() => { setGenerationError(null); setAutoRetryCount(0); }}
              autoRetryCount={autoRetryCount} onAutoRetryStart={() => setAutoRetryCount((c) => c + 1)} />
          )}
        </AnimatePresence>

        <div className="flex items-center gap-3">
          <Button onClick={handleGenerateAll} disabled={generating || validOutputs.length === 0}
            className="flex-1 bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold h-12 rounded-md text-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(255,68,0,0.3)] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="bulk-generate-button">
            {generating ? (<><Spinner size={18} className="animate-spin" />Generating...</>) : (<><Lightning size={18} weight="fill" />Generate {validOutputs.length} Piece{validOutputs.length !== 1 ? "s" : ""}</>)}
          </Button>
          {successCount > 0 && !generating && (
            <Button onClick={handleSaveAll}
              className="bg-[var(--bg-raised)] border border-[var(--border-strong)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] h-12 px-6 rounded-md text-sm transition-colors"
              data-testid="bulk-save-all-button">
              <FloppyDisk size={16} className="mr-2" />Save All ({successCount})
            </Button>
          )}
        </div>
      </main>
    </div>
    <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="bulk generate" />
    </NoBusinessGuard>
  );
}
